import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Edit2,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import axiosInstance from '../../../api/axios.js';
import { resolve403ErrorMessage } from '../../../request/getApiErrorMessage.ts';
import { controlRecordToFormValues, formValuesToApiPayload } from '../functional.ts';
import { mergeGeneratedPopulationRequirement } from '../populationRequirement.ts';
import {
  buildGenerateTestStepsRequest,
  generateTestStepsStream,
  type GeneratedControlSpec,
} from '../generateTestStepsStream.ts';

type TestStep = {
  summary?: string;
  description?: string;
  expected_outcome?: string;
  responsible_party?: string;
  audit_procedure?: string;
  test_attributes?: unknown[];
  [key: string]: unknown;
};

type ModalStage = 'generating' | 'review' | 'error';

export const ControlConfigureTestModal: React.FC<{
  open: boolean;
  controlUuid: string;
  control: Record<string, unknown>;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ open, controlUuid, control, onClose, onSuccess }) => {
  const titleId = useId();
  const abortRef = useRef<AbortController | null>(null);
  const [stage, setStage] = useState<ModalStage>('generating');
  const [editableSpec, setEditableSpec] = useState<GeneratedControlSpec | null>(null);
  const [isEditingSteps, setIsEditingSteps] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Preparing to generate test steps...');
  const [loadingDetail, setLoadingDetail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    onClose();
  }, [onClose]);

  const runGeneration = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStage('generating');
    setError(null);
    setEditableSpec(null);
    setLoadingMessage('Preparing to generate test steps...');
    setLoadingDetail(null);

    try {
      const requestBody = buildGenerateTestStepsRequest(control);
      const result = await generateTestStepsStream(
        requestBody,
        {
          onStatus: (payload) => {
            if (payload.message) setLoadingMessage(payload.message);
            if (payload.detail) setLoadingDetail(payload.detail);
          },
        },
        controller.signal
      );

      if (controller.signal.aborted) return;

      const merged: GeneratedControlSpec = {
        ...result,
        control_id: String(control.control_id ?? result.control_id ?? ''),
        control_title: String(control.title ?? result.control_title ?? ''),
        control_objective: String(control.objective ?? result.control_objective ?? ''),
        control_wording_text: String(control.control_wording_text ?? result.control_wording_text ?? ''),
        control_wording_raw: String(control.control_wording_text ?? result.control_wording_raw ?? ''),
        frequency: String(control.frequency ?? result.frequency ?? ''),
      };

      setEditableSpec(merged);
      setStage('review');
    } catch (err) {
      if (controller.signal.aborted) return;
      const message = resolve403ErrorMessage(
        err,
        err instanceof Error ? err.message : 'Failed to generate test steps'
      );
      setError(message);
      setStage('error');
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  }, [control]);

  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      abortRef.current = null;
      setStage('generating');
      setEditableSpec(null);
      setError(null);
      setIsEditingSteps(false);
      return;
    }
    void runGeneration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSaving) {
        event.preventDefault();
        handleClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, isSaving, handleClose]);

  const handleTestStepChange = (index: number, field: string, value: string) => {
    setEditableSpec((prev) => {
      if (!prev?.test_steps) return prev;
      const steps = [...prev.test_steps];
      steps[index] = { ...steps[index], [field]: value };
      return { ...prev, test_steps: steps };
    });
  };

  const addTestStep = () => {
    setEditableSpec((prev) => ({
      ...prev,
      test_steps: [
        ...(prev?.test_steps ?? []),
        {
          summary: 'New test step',
          description: '',
          expected_outcome: '',
          responsible_party: 'preparer',
          audit_procedure: 'inspection',
        },
      ],
    }));
  };

  const removeTestStep = (index: number) => {
    setEditableSpec((prev) => ({
      ...prev,
      test_steps: (prev?.test_steps ?? []).filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!editableSpec?.test_steps?.length) return;
    setIsSaving(true);
    try {
      const formValues = controlRecordToFormValues(control);
      // Keep the AI's population decision (an earlier auditor override is preserved).
      const samplingAttributes = mergeGeneratedPopulationRequirement(
        (control as { sampling_attributes?: unknown }).sampling_attributes,
        editableSpec,
      );
      const payload = {
        ...formValuesToApiPayload(formValues, { controlUuid }),
        test_steps: editableSpec.test_steps,
        status: 'active',
        ...(samplingAttributes ? { sampling_attributes: samplingAttributes } : {}),
      };
      const userHash = localStorage.getItem('userHash') || '';
      await axiosInstance.put(`/api/sox-controls/${controlUuid}`, payload, {
        headers: { Authorization: userHash },
      });
      onSuccess();
      handleClose();
    } catch (err) {
      setError(resolve403ErrorMessage(err, 'Failed to save test steps. Please try again.'));
      setStage('error');
    } finally {
      setIsSaving(false);
    }
  };

  const canClose = !isSaving;

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        className="absolute inset-0 bg-black/50"
        aria-hidden="true"
        onClick={() => {
          if (canClose) handleClose();
        }}
      />
      <div
        className="relative z-10 flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 bg-gray-50 px-6 py-4 shrink-0">
          <div className="min-w-0 pr-2">
            <h2 id={titleId} className="text-xl font-semibold text-gray-900">
              Control Creation Wizard
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {stage === 'generating'
                ? 'Generating test steps'
                : stage === 'review'
                  ? 'Review AI-generated test steps'
                  : 'Configure test'}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {stage === 'review' && (
              <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-medium">
                Step 2 of 2
              </span>
            )}
            {canClose && (
              <button
                type="button"
                onClick={handleClose}
                disabled={isSaving}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Close wizard"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {stage === 'generating' && (
            <div className="bg-white rounded-xl border border-gray-200 p-10">
              <div className="flex flex-col items-center space-y-8">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-blue-400 opacity-20 animate-ping" />
                  <div className="relative w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600">
                    <Loader2 className="w-10 h-10 text-white animate-spin" />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                </div>
                <div className="text-center space-y-3">
                  <h3 className="text-xl font-bold text-gray-900">Generating Test Steps</h3>
                  <p className="text-sm text-gray-600">AI is creating comprehensive test procedures...</p>
                </div>
                <div className="w-full max-w-md bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100">
                  <p className="text-sm text-blue-900 font-medium text-center">{loadingMessage}</p>
                  {loadingDetail ? (
                    <p className="text-xs text-blue-700 text-center mt-2 opacity-75">{loadingDetail}</p>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {stage === 'error' && (
            <div className="flex flex-col items-center py-12 space-y-4">
              <AlertCircle className="h-10 w-10 text-red-500" />
              <p className="text-sm text-red-700 text-center">{error}</p>
              <button
                type="button"
                onClick={() => void runGeneration()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Try Again
              </button>
            </div>
          )}

          {stage === 'review' && editableSpec && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Review Control &amp; Test Steps</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Review the AI-generated test steps. You can edit them before saving.
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4 text-sm">
                <div className="flex flex-wrap items-center gap-6">
                  <div>
                    <span className="text-gray-500">ID:</span>
                    <span className="ml-1 font-semibold text-gray-900">{editableSpec.control_id}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Frequency:</span>
                    <span className="ml-1 text-gray-900">{editableSpec.frequency}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-gray-500">Title:</span>
                    <span className="ml-1 text-gray-900">{editableSpec.control_title}</span>
                  </div>
                </div>
                {(editableSpec.control_wording_text || editableSpec.control_wording_raw) && (
                  <div className="border-t border-gray-200 pt-3">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Control Wording
                    </span>
                    <p className="mt-1 text-sm text-gray-700 leading-relaxed">
                      {editableSpec.control_wording_text || editableSpec.control_wording_raw}
                    </p>
                  </div>
                )}
                {editableSpec.control_objective ? (
                  <div className="border-t border-gray-200 pt-3">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Control Objective
                    </span>
                    <p className="mt-1 text-sm text-gray-700 leading-relaxed">{editableSpec.control_objective}</p>
                  </div>
                ) : null}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-500" />
                  <h4 className="text-sm font-semibold text-gray-900">
                    AI-Generated Test Steps ({editableSpec.test_steps?.length ?? 0})
                  </h4>
                </div>
                <div className="flex items-center gap-2">
                  {isEditingSteps && (
                    <button
                      type="button"
                      onClick={addTestStep}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                    >
                      <Plus className="h-3 w-3" />
                      Add Step
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsEditingSteps((v) => !v)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md ${
                      isEditingSteps
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Edit2 className="h-3 w-3" />
                    {isEditingSteps ? 'Done Editing' : 'Edit'}
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                {(editableSpec.test_steps as TestStep[]).map((step, index) => (
                  <div key={index} className="p-4 flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-gray-900 text-white text-xs font-medium rounded-full flex items-center justify-center mt-1">
                      {index + 1}
                    </span>
                    {isEditingSteps ? (
                      <textarea
                        value={step.summary || ''}
                        onChange={(e) => handleTestStepChange(index, 'summary', e.target.value)}
                        className="flex-1 border border-gray-200 rounded px-3 py-2 text-sm min-h-[60px] focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <p className="flex-1 text-sm text-gray-700 leading-relaxed">{step.summary}</p>
                    )}
                    {isEditingSteps && (
                      <button
                        type="button"
                        onClick={() => removeTestStep(index)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                        title="Delete step"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {stage === 'review' && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 bg-gray-50/80 px-6 py-4 shrink-0">
            <button
              type="button"
              onClick={() => void runGeneration()}
              disabled={isSaving}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Regenerate
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={isSaving || !editableSpec?.test_steps?.length}
              className="inline-flex items-center px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Save Control
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ControlConfigureTestModal;
